import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Briefcase,
  Clock,
  Gauge,
  Tag,
  SlidersHorizontal,
  Eye,
  EyeOff,
  Check,
  Play,
  ListChecks,
  Timer,
  ChevronLeft,
  ChevronDown,
  X,
  BookOpen,
  RotateCcw,
  ClipboardList,
  BarChart3,
  Minus,
  Plus,
} from "lucide-react";
import MatchmakingSpinner from "@/components/MatchmakingSpinner";
import CompletionCard from "@/components/CompletionCard";
import ToggleChips from "@/components/ToggleChips";
import AIFeedbackPanel from "@/components/AIFeedbackPanel";
import RichTextEditor from "@/components/RichTextEditor";
import Markdown from "@/components/Markdown";
import { useInterviewFilterStore } from "@/store/interviewFilterStore";
import { useProgressStore } from "@/store/progressStore";
import {
  DIFFICULTIES,
  LEVELS,
  levelName,
  levelOf,
  questionsForFilters,
  generateQuestion,
  pickMockSet,
  type InterviewQuestion,
} from "@/content/interview";
import { useInterviewRoles, useInterviewTypes, useQuestions, getQuestions, roleName, typeName } from "@/store/customContentStore";
import type { Difficulty } from "@/core/data/types";
import type { CompletionResult } from "@/core/data/db";

type Phase = "idle" | "searching" | "active" | "summary";
type Mode = "single" | "mock";

const XP_BY_DIFFICULTY: Record<Difficulty, number> = { Easy: 10, Medium: 20, Hard: 30 };
const MOCK_LENGTHS = [3, 5, 10];
const draftKey = (id: string) => `hippo.idraft.${id}`;
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export default function Interview() {
  const { roles, types, difficulties, levels, toggleRole, toggleType, toggleDifficulty, toggleLevel, setAllRoles, setAllTypes, setAllDifficulties, setAllLevels } =
    useInterviewFilterStore();
  const allRoles = useInterviewRoles();
  const allTypes = useInterviewTypes();
  const questions = useQuestions();
  const complete = useProgressStore((s) => s.complete);

  const [phase, setPhase] = useState<Phase>("idle");
  const [mode, setMode] = useState<Mode>("single");
  const [showFilters, setShowFilters] = useState(false);
  const [mockLength, setMockLength] = useState(5);

  const [question, setQuestion] = useState<InterviewQuestion | null>(null);
  const [completion, setCompletion] = useState<CompletionResult | null>(null);

  const [mockSet, setMockSet] = useState<InterviewQuestion[]>([]);
  const [mockIdx, setMockIdx] = useState(0);
  const [mockXp, setMockXp] = useState(0);
  const [mockResponses, setMockResponses] = useState<Record<string, string>>({});
  const [elapsed, setElapsed] = useState(0);

  const [response, setResponse] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [covered, setCovered] = useState<Set<number>>(new Set());

  const lastId = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const startTs = useRef(0);

  const available = useMemo(
    () => questionsForFilters(roles, types, difficulties, levels, questions).length,
    [roles, types, difficulties, levels, questions]
  );
  const currentQ = mode === "mock" ? mockSet[mockIdx] : question;
  const maxMock = Math.max(2, Math.min(available, 50));

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  // Keep the chosen mock length within what's currently available.
  useEffect(() => {
    setMockLength((n) => Math.max(2, Math.min(n, Math.max(2, available))));
  }, [available]);

  useEffect(() => {
    if (phase !== "active" || mode !== "mock") return;
    const id = window.setInterval(() => setElapsed(Math.floor((Date.now() - startTs.current) / 1000)), 1000);
    return () => window.clearInterval(id);
  }, [phase, mode]);

  useEffect(() => {
    if (currentQ) localStorage.setItem(draftKey(currentQ.id), response);
  }, [response, currentQ]);

  function loadQuestion(q: InterviewQuestion) {
    setRevealed(false);
    setShowExample(false);
    setCovered(new Set());
    setResponse(localStorage.getItem(draftKey(q.id)) ?? "");
  }

  function startSingle() {
    if (available === 0) return;
    setMode("single");
    setCompletion(null);
    setPhase("searching");
    timer.current = window.setTimeout(() => {
      const q = generateQuestion(roles, types, difficulties, levels, lastId.current, getQuestions());
      if (!q) return setPhase("idle");
      lastId.current = q.id;
      setQuestion(q);
      loadQuestion(q);
      setPhase("active");
    }, 2400);
  }

  function startMock() {
    const set = pickMockSet(roles, types, mockLength, difficulties, levels, getQuestions());
    if (set.length < 2) return;
    setMode("mock");
    setMockSet(set);
    setMockIdx(0);
    setMockXp(0);
    setMockResponses({});
    setElapsed(0);
    startTs.current = Date.now();
    setPhase("searching");
    timer.current = window.setTimeout(() => {
      loadQuestion(set[0]);
      setPhase("active");
    }, 2400);
  }

  function exitToIdle() {
    if (timer.current) window.clearTimeout(timer.current);
    setPhase("idle");
  }

  function xpFor(q: InterviewQuestion) {
    return XP_BY_DIFFICULTY[q.difficulty] + (response.trim().length >= 150 ? 5 : 0);
  }

  async function saveAttempt(q: InterviewQuestion, score?: number): Promise<CompletionResult> {
    const result = await complete(
      {
        refType: "interview",
        refId: q.id,
        categoryIds: [q.roleId],
        title: q.prompt,
        context: `${roleName(q.roleId)} · ${typeName(q.typeId)}`,
        response: response.trim(),
        score,
        createdAt: Date.now(),
      },
      xpFor(q)
    );
    localStorage.removeItem(draftKey(q.id));
    return result;
  }

  async function submitSingle() {
    if (!question) return;
    const total = question.idealPoints.length;
    const score = revealed && total > 0 ? Math.round((covered.size / total) * 100) : undefined;
    setCompletion(await saveAttempt(question, score));
  }

  async function nextMock() {
    const q = mockSet[mockIdx];
    setMockResponses((m) => ({ ...m, [q.id]: response.trim() }));
    if (response.trim().length > 0) {
      const r = await saveAttempt(q);
      setMockXp((x) => x + r.xpGained);
    }
    if (mockIdx + 1 >= mockSet.length) setPhase("summary");
    else {
      const ni = mockIdx + 1;
      setMockIdx(ni);
      loadQuestion(mockSet[ni]);
    }
  }

  function toggleCovered(i: number) {
    setCovered((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  // ---- Searching ----
  if (phase === "searching") {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <MatchmakingSpinner
          onCancel={exitToIdle}
          label={mode === "mock" ? "Setting up your mock interview" : "Finding a question"}
          statusMessages={["Reviewing the question bank…", "Picking a fair question…", "Checking the rubric…", "Almost ready…"]}
        />
      </div>
    );
  }

  // ---- Mock summary ----
  if (phase === "summary") {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="rounded-2xl border border-primary/40 bg-primary/5 p-7 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-fg shadow-glow">
            <ListChecks className="h-7 w-7" />
          </div>
          <h2 className="mt-4 font-display text-2xl font-bold text-text">Mock interview complete</h2>
          <p className="mt-1 text-muted">Saved to your history.</p>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <SummaryStat label="Questions" value={`${mockSet.length}`} />
            <SummaryStat label="XP gained" value={`+${mockXp}`} />
            <SummaryStat label="Time" value={mmss(elapsed)} />
          </div>
        </div>

        <div>
          <h3 className="mb-3 font-display text-sm font-semibold uppercase tracking-wide text-muted">Review your answers</h3>
          <div className="space-y-2">
            {mockSet.map((q) => (
              <ReviewItem key={q.id} q={q} answer={mockResponses[q.id] ?? ""} />
            ))}
          </div>
        </div>

        <button onClick={() => setPhase("idle")} className="w-full rounded-lg bg-primary px-4 py-2.5 font-semibold text-primary-fg transition-transform hover:scale-[1.01]">
          Done
        </button>
      </div>
    );
  }

  // ---- Active question ----
  if (phase === "active" && currentQ) {
    const q = currentQ;
    const total = q.idealPoints.length;
    const score = total > 0 ? Math.round((covered.size / total) * 100) : 0;
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 24 }} className="mx-auto max-w-3xl space-y-5">
        {/* Top bar: back/exit + mock progress */}
        <div className="flex items-center justify-between">
          {mode === "mock" ? (
            <button onClick={exitToIdle} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted transition-colors hover:text-text">
              <X className="h-4 w-4" /> Exit interview
            </button>
          ) : (
            <button onClick={exitToIdle} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted transition-colors hover:text-text">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
          )}
          {mode === "mock" && (
            <div className="flex items-center gap-3 text-sm text-muted">
              <span className="font-medium text-text">Q {mockIdx + 1} / {mockSet.length}</span>
              <span className="inline-flex items-center gap-1.5"><Timer className="h-4 w-4" /> {mmss(elapsed)}</span>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-border bg-surface p-7">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 font-medium text-primary">
              <Briefcase className="h-3.5 w-3.5" /> {roleName(q.roleId)}
            </span>
            <Badge icon={Tag}>{typeName(q.typeId)}</Badge>
            <Badge icon={Gauge}>{q.difficulty}</Badge>
            <Badge icon={BarChart3}>{levelName(levelOf(q))}</Badge>
            <Badge icon={Clock}>Suggested {q.estMinutes} min</Badge>
          </div>

          <h2 className="mt-4 font-display text-xl font-bold leading-snug text-text">{q.prompt}</h2>

          {q.deliverable && (
            <div className="mt-4 flex gap-3 rounded-xl border border-primary/40 bg-primary/5 p-4">
              <ClipboardList className="h-5 w-5 shrink-0 text-primary" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-primary">Deliverable — make or bring something real</div>
                <p className="mt-0.5 text-sm text-text">{q.deliverable}</p>
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={() => setRevealed((v) => !v)} className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-1.5 text-sm font-medium text-text transition-colors hover:bg-border/40">
              {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {revealed ? "Hide key points" : "Reveal key points"}
            </button>
            <button onClick={() => setShowExample((v) => !v)} className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-1.5 text-sm font-medium text-text transition-colors hover:bg-border/40">
              <BookOpen className="h-4 w-4" />
              {showExample ? "Hide example answer" : "Show example answer"}
            </button>
          </div>

          {revealed && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 overflow-hidden rounded-xl border border-accent/30 bg-accent/5 p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-accent">Key points {mode === "single" ? "— tap the ones you covered" : ""}</span>
                {mode === "single" && <span className="text-xs font-medium text-text">{covered.size}/{total} · {score}%</span>}
              </div>
              <ul className="space-y-1.5">
                {q.idealPoints.map((p, i) => {
                  const on = covered.has(i);
                  const interactive = mode === "single";
                  return (
                    <li key={p}>
                      <button
                        onClick={interactive ? () => toggleCovered(i) : undefined}
                        className={[
                          "flex w-full items-start gap-2 rounded-md px-2 py-1 text-left text-sm",
                          interactive ? "transition-colors hover:bg-accent/10" : "cursor-default",
                        ].join(" ")}
                      >
                        <span className={["mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border", on ? "border-accent bg-accent text-bg" : "border-border"].join(" ")}>
                          {on && <Check className="h-3 w-3" />}
                        </span>
                        <span className="text-text">{p}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </motion.div>
          )}

          {showExample && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 overflow-hidden rounded-xl border border-border bg-surface-2/60 p-4">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Example answer</div>
              <div className="text-sm leading-relaxed text-muted"><Markdown source={q.exampleAnswer} /></div>
            </motion.div>
          )}
        </div>

        {completion ? (
          <div className="space-y-3">
            {typeof completion === "object" && revealed && (
              <div className="rounded-xl border border-border bg-surface px-4 py-2.5 text-center text-sm text-muted">
                Self-assessment: covered <span className="text-text">{covered.size}/{total}</span> key points ({score}%)
              </div>
            )}
            <CompletionCard result={completion} onNext={startSingle} nextLabel="Next question" />
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-surface p-7">
            <label className="text-sm font-medium text-text">Your answer</label>
            <div className="mt-2">
              <RichTextEditor
                value={response}
                onChange={setResponse}
                placeholder="Structure your answer — think out loud… (autosaved)"
                minHeight="9rem"
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {mode === "mock" ? (
                <button onClick={nextMock} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]">
                  {mockIdx + 1 >= mockSet.length ? "Finish interview" : "Next question"}
                </button>
              ) : (
                <button onClick={submitSingle} disabled={response.trim().length === 0} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-40">
                  <Check className="h-4 w-4" /> Save &amp; complete
                </button>
              )}
              {mode === "single" && (
                <button onClick={startSingle} className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-4 py-2 text-sm font-medium text-text transition-colors hover:bg-border/40">
                  <RotateCcw className="h-4 w-4" /> Skip
                </button>
              )}
              {mode === "single" &&
                (response.trim().length > 0 ? (
                  <AIFeedbackPanel
                    buildRequest={() => ({
                      kind: "interview",
                      title: q.prompt,
                      task: q.prompt,
                      context: `${roleName(q.roleId)} · ${typeName(q.typeId)}`,
                      idealPoints: q.idealPoints,
                      response: response.trim(),
                    })}
                  />
                ) : (
                  <span className="ml-auto text-xs text-muted/70">Write an answer to get AI feedback</span>
                ))}
            </div>
          </div>
        )}
      </motion.div>
    );
  }

  // ---- Idle ----
  const canMock = available >= 2;
  return (
    <div className="mx-auto max-w-2xl">
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="grid h-16 w-16 place-items-center rounded-2xl bg-surface-2 text-primary">
          <Briefcase className="h-8 w-8" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-text">Prep for your game design interview</h2>
          <p className="mt-2 text-muted">
            Practice real questions across roles and formats. {available} question{available === 1 ? "" : "s"} match your filters.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <button onClick={startSingle} disabled={available === 0} className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 font-display font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.03] enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-40">
            <Play className="h-4 w-4" /> Practice a Question
          </button>
          <button onClick={() => setShowFilters((v) => !v)} className={["inline-flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium transition-colors", showFilters ? "border-primary bg-primary/10 text-text" : "border-border bg-surface text-text hover:bg-surface-2"].join(" ")}>
            <SlidersHorizontal className="h-4 w-4" /> Filters
          </button>
        </div>

        {/* Mock interview — choose any number of questions */}
        <div className="flex flex-wrap items-center justify-center gap-2 rounded-xl border border-border bg-surface px-3 py-2">
          <ListChecks className="h-4 w-4 text-muted" />
          <span className="text-sm text-muted">Mock interview:</span>
          {MOCK_LENGTHS.map((n) => (
            <button
              key={n}
              onClick={() => setMockLength(Math.max(2, Math.min(n, maxMock)))}
              className={["rounded-md px-2.5 py-1 text-sm font-medium transition-colors", mockLength === n ? "bg-primary text-primary-fg" : "text-muted hover:text-text"].join(" ")}
            >
              {n}
            </button>
          ))}
          <div className="ml-1 inline-flex items-center gap-1 rounded-md border border-border bg-surface-2 px-1 py-0.5">
            <button onClick={() => setMockLength((n) => Math.max(2, n - 1))} disabled={mockLength <= 2} aria-label="Fewer questions" className="grid h-6 w-6 place-items-center rounded text-muted hover:text-text disabled:opacity-30">
              <Minus className="h-3.5 w-3.5" />
            </button>
            <input
              type="number"
              value={mockLength}
              min={2}
              max={maxMock}
              onChange={(e) => setMockLength(Math.max(2, Math.min(Number(e.target.value) || 2, maxMock)))}
              aria-label="Number of questions"
              className="w-10 bg-transparent text-center text-sm font-medium text-text focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button onClick={() => setMockLength((n) => Math.min(maxMock, n + 1))} disabled={mockLength >= maxMock} aria-label="More questions" className="grid h-6 w-6 place-items-center rounded text-muted hover:text-text disabled:opacity-30">
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
          <button onClick={startMock} disabled={!canMock} title={canMock ? undefined : "Enable more filters"} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-sm font-semibold text-primary-fg shadow-glow transition-transform enabled:hover:scale-[1.03] disabled:cursor-not-allowed disabled:opacity-40">
            <Play className="h-3.5 w-3.5" /> Start {Math.min(mockLength, available)}
          </button>
        </div>

        {available === 0 && <p className="text-sm text-amber-400">No questions match — enable some filters below.</p>}
      </div>

      {showFilters && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-8 space-y-6 rounded-2xl border border-border bg-surface p-6">
          <ToggleChips label="Roles" items={allRoles.map((r) => ({ id: r.id, name: r.name, description: r.description }))} activeIds={roles} onToggle={toggleRole} onSetAll={setAllRoles} />
          <ToggleChips label="Question types" items={allTypes.map((t) => ({ id: t.id, name: t.name, description: t.description }))} activeIds={types} onToggle={toggleType} onSetAll={setAllTypes} />
          <ToggleChips label="Difficulty" items={DIFFICULTIES.map((d) => ({ id: d, name: d }))} activeIds={difficulties} onToggle={toggleDifficulty} onSetAll={setAllDifficulties} />
          <ToggleChips label="Designer level" items={LEVELS.map((l) => ({ id: l.id, name: l.name }))} activeIds={levels} onToggle={toggleLevel} onSetAll={setAllLevels} />
          <p className="text-xs text-muted">Questions inherit a level from their difficulty (Easy → Junior, Medium → Senior, Hard → Lead) until you tag an exact level in Content Studio.</p>
        </motion.div>
      )}
    </div>
  );
}

function ReviewItem({ q, answer }: { q: InterviewQuestion; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-3 px-4 py-3 text-left">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-text">{q.prompt}</div>
          <div className="text-xs text-muted">{roleName(q.roleId)} · {typeName(q.typeId)} · {levelName(levelOf(q))}</div>
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-3 border-t border-border p-4 text-sm">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted">Your answer</div>
            <p className="mt-1 whitespace-pre-wrap text-text">{answer || <span className="text-muted">(skipped)</span>}</p>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-accent">Key points</div>
            <ul className="mt-1 ml-4 list-disc text-muted">{q.idealPoints.map((p) => <li key={p}>{p}</li>)}</ul>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-muted">Example answer</div>
            <div className="mt-1 leading-relaxed text-muted"><Markdown source={q.exampleAnswer} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-3">
      <div className="font-display text-lg font-bold text-text">{value}</div>
      <div className="text-[11px] text-muted">{label}</div>
    </div>
  );
}

function Badge({ icon: Icon, children }: { icon: typeof Clock; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 px-3 py-1 font-medium text-muted">
      <Icon className="h-3.5 w-3.5" />
      {children}
    </span>
  );
}
